import { Suspense } from 'react';

import Config from '@config';
import {
  APP_REFRESH_ID,
  ConfirmProvider,
  DISTRIBUTION_GRANTABLE_SERVICES,
  ErrorBoundary,
  FeatureFlagProvider,
  GrantableServicesProvider,
  KendoLocaleProvider,
  LocalizationProvider,
  NotificationProvider,
  SERVICE_IDS,
  ThemeProvider,
} from '@gasbi/shared';
import { I18nProvider, initI18nInstance } from '@gasbi/shared/common/providers';
import '@gasbi/shared/common/utils/extendYupMethods';
import '@progress/kendo-theme-default/dist/all.css';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { GlobalLoadingScreen } from '@/components/GlobalLoadingScreen';
import { type Flags, defaultFlags } from '@/featureFlagsConfig';
import { i18nInstanceOptions } from '@/locals';

import { LicenseInfo } from '@mui/x-license';

import store from './app/store';
import { getRoutes } from './route';
import './styles/inter-font.css';

LicenseInfo.setLicenseKey(Config.VITE_MUI_LICENSE);

OverlayScrollbars.plugin(ClickScrollPlugin);

localStorage.setItem(APP_REFRESH_ID, Date.now().toString());

dayjs.locale('uk');

async function bootstrap() {
  const i18n = await initI18nInstance(i18nInstanceOptions);

  createRoot(document.getElementById('root')!).render(
    <>
      <ErrorBoundary>
        <I18nProvider instance={i18n}>
          <FeatureFlagProvider<Flags> defaultFlags={defaultFlags}>
            <Provider store={store}>
              <ThemeProvider>
                <LocalizationProvider>
                  <KendoLocaleProvider>
                    <Suspense>
                      <NotificationProvider>
                        <ConfirmProvider>
                          {/* The shared employee forms grant the Distribution group's services (Epic GSB-8312) */}
                          <GrantableServicesProvider
                            services={DISTRIBUTION_GRANTABLE_SERVICES}
                            showDecorative={false}
                            adminServiceId={SERVICE_IDS.DISTRIBUTION_ADMIN}
                          >
                            <RouterProvider router={getRoutes()} />
                          </GrantableServicesProvider>
                        </ConfirmProvider>
                      </NotificationProvider>
                    </Suspense>
                  </KendoLocaleProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </Provider>
          </FeatureFlagProvider>
        </I18nProvider>
      </ErrorBoundary>
      <GlobalLoadingScreen />
    </>,
  );
}

bootstrap();
